/*!
 * Blog Shortcodes for Motion Theme - GraphQL Version (GID Edition)
 * Uses Shopify Storefront API with direct product ID lookups
 */

window.BlogShortcodes = (function () {
	'use strict';

	// Configuration
	const config = {
		productShortcodeRegex: /\[product:(\d+)\]/g,
		collectionShortcodeRegex: /\[collection:([A-Za-z0-9]+)\]/g,
		loadingClass: 'shortcode-loading',
		errorClass: 'shortcode-error',
		debug: true,

		// Storefront API Configuration
		shopDomain: 'g7wunf-ab.myshopify.com', // Your shop domain
		storefrontToken: '3619dd593ee6a2ce86ab870b63068c0a', // Your access token
		apiVersion: '2023-10'
	};

	// Cache for processed items
	const cache = {
		products: new Map(),
		collections: new Map()
	};

	// Loading states
	const loadingStates = {
		products: new Set(),
		collections: new Set()
	};

	// Track floated product indices for stacking
	let floatedProductIndex = 0;

	/**
	 * Log debug messages
	 */
	function log(...args) {
		if (config.debug) {
			console.log('[BlogShortcodes]', ...args);
		}
	}

	/**
	 * Make GraphQL request to Storefront API
	 */
	async function graphqlRequest(query, variables = {}) {
		const url = `https://${config.shopDomain}/api/${config.apiVersion}/graphql.json`;

		const response = await fetch(url, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				'X-Shopify-Storefront-Access-Token': config.storefrontToken
			},
			body: JSON.stringify({ query, variables })
		});

		if (!response.ok) {
			throw new Error(
				`GraphQL request failed: ${response.status} ${response.statusText}`
			);
		}

		const data = await response.json();

		if (data.errors) {
			throw new Error(
				`GraphQL errors: ${data.errors
					.map((e) => e.message)
					.join(', ')}`
			);
		}

		return data.data;
	}

	/**
	 * Fetch product by GID using direct lookup
	 */
	async function fetchProductById(productId) {
		const productGid = `gid://shopify/Product/${productId}`;

		// Check cache using the full GID
		if (cache.products.has(productGid)) {
			return cache.products.get(productGid);
		}

		// Check if already loading
		if (loadingStates.products.has(productGid)) {
			while (loadingStates.products.has(productGid)) {
				await new Promise((resolve) => setTimeout(resolve, 50));
			}
			return cache.products.get(productGid);
		}

		loadingStates.products.add(productGid);

		try {
			log('Fetching product by GID:', productGid);

			// GraphQL query to get product by ID
			const query = `
				query getProduct($id: ID!) {
					product(id: $id) {
						id
						title
						handle
						description
						vendor
						productType
						tags
						availableForSale
						collections(first: 5) {
							edges {
								node {
									id
									title
									handle
								}
							}
						}
						priceRange {
							minVariantPrice {
								amount
								currencyCode
							}
							maxVariantPrice {
								amount
								currencyCode
							}
						}
						compareAtPriceRange {
							minVariantPrice {
								amount
								currencyCode
							}
							maxVariantPrice {
								amount
								currencyCode
							}
						}
						featuredImage {
							url
							altText
							width
							height
						}
						images(first: 5) {
							edges {
								node {
									url
									altText
									width
									height
								}
							}
						}
						variants(first: 10) {
							edges {
								node {
									id
									title
									availableForSale
									price {
										amount
										currencyCode
									}
									compareAtPrice {
										amount
										currencyCode
									}
								}
							}
						}
					}
				}
			`;

			const data = await graphqlRequest(query, { id: productGid });

			if (!data.product) {
				throw new Error(`Product with ID "${productGid}" not found`);
			}

			// Transform the GraphQL product data
			const transformedProduct = transformGraphQLProduct(data.product);
			cache.products.set(productGid, transformedProduct);

			log('Product fetched successfully:', transformedProduct.title);

			return transformedProduct;
		} catch (error) {
			log('Error fetching product:', error);
			cache.products.set(productGid, null);
			throw error;
		} finally {
			loadingStates.products.delete(productGid);
		}
	}

	/**
	 * Fetch collection by numeric ID using direct lookup
	 */
	async function fetchCollectionById(collectionId) {
		// Construct the GID from numeric ID
		const collectionGid = `gid://shopify/Collection/${collectionId}`;

		// Check cache using the numeric ID as key
		if (cache.collections.has(collectionId)) {
			return cache.collections.get(collectionId);
		}

		// Check if already loading
		if (loadingStates.collections.has(collectionId)) {
			while (loadingStates.collections.has(collectionId)) {
				await new Promise((resolve) => setTimeout(resolve, 50));
			}
			return cache.collections.get(collectionId);
		}

		loadingStates.collections.add(collectionId);

		try {
			log(
				'Fetching collection by ID:',
				collectionId,
				'GID:',
				collectionGid
			);

			// GraphQL query to get collection by ID
			const query = `
			query getCollection($id: ID!) {
				collection(id: $id) {
					id
					title
					handle
					description
					products(first: 16) {
						edges {
							node {
								id
								title
								handle
								vendor
								priceRange {
									minVariantPrice {
										amount
										currencyCode
									}
								}
								compareAtPriceRange {
									minVariantPrice {
										amount
										currencyCode
									}
								}
								featuredImage {
									url
									altText
								}
							}
						}
					}
				}
			}
		`;

			const data = await graphqlRequest(query, { id: collectionGid });

			if (!data.collection) {
				throw new Error(
					`Collection with ID "${collectionId}" not found. Make sure the collection is published to your Online Store sales channel.`
				);
			}

			const collection = data.collection;

			// Transform the GraphQL collection data
			const transformedCollection = {
				id: collection.id.split('/').pop(),
				title: collection.title,
				handle: collection.handle,
				description: collection.description,
				products_count: collection.products.edges.length,
				products: collection.products.edges.map((productEdge) => ({
					id: productEdge.node.id.split('/').pop(),
					title: productEdge.node.title,
					handle: productEdge.node.handle,
					vendor: productEdge.node.vendor,
					price: Math.round(
						parseFloat(
							productEdge.node.priceRange.minVariantPrice.amount
						) * 100
					),
					compare_at_price: productEdge.node.compareAtPriceRange
						?.minVariantPrice
						? Math.round(
								parseFloat(
									productEdge.node.compareAtPriceRange
										.minVariantPrice.amount
								) * 100
							)
						: null,
					currency_code: productEdge.node.priceRange.minVariantPrice.currencyCode,
					featured_image: productEdge.node.featuredImage?.url || null
				}))
			};

			cache.collections.set(collectionId, transformedCollection);

			log(
				'Collection fetched successfully:',
				transformedCollection.title
			);

			return transformedCollection;
		} catch (error) {
			log('Error fetching collection:', error);
			cache.collections.set(collectionId, null);
			throw error;
		} finally {
			loadingStates.collections.delete(collectionId);
		}
	}

	/**
	 * Detect and integrate with theme's currency conversion system
	 */
	function detectCurrencySystem() {
		// Check for common currency conversion systems
		const systems = {
			shopifyNative: window.Shopify?.currency,
			shopifyTheme: window.theme?.Currency || window.Currency,
			cartSDK: window.ShopifyAnalytics?.meta?.currency,
			boldCurrency: window.BOLD,
			currencyConverter: window.Currency || window.CurrencyConverter,
			customConverter: document.querySelector('[data-currency-converter]')
		};

		log('Detected currency systems:', systems);
		return systems;
	}

	/**
	 * Get current selected currency from theme
	 */
	function getCurrentCurrency() {
		// Try multiple methods to get current currency

		// Method 1: Shopify native currency
		if (window.Shopify?.currency?.active) {
			return window.Shopify.currency.active;
		}

		// Method 2: Theme currency object
		if (window.theme?.Currency?.currentCurrency) {
			return window.theme.Currency.currentCurrency;
		}

		// Method 3: Check for currency selector elements
		const currencySelector = document.querySelector('select[name="currency"], [data-currency-picker] select, .currency-selector select');
		if (currencySelector?.value) {
			return currencySelector.value;
		}

		// Method 4: Check for active currency in DOM
		const activeCurrency = document.querySelector('[data-currency].active, .currency-item.active');
		if (activeCurrency) {
			return activeCurrency.dataset.currency || activeCurrency.textContent.trim();
		}

		// Method 5: Check localStorage/sessionStorage
		const storedCurrency = localStorage.getItem('currency') || sessionStorage.getItem('currency');
		if (storedCurrency) {
			return storedCurrency;
		}

		// Default fallback
		return null;
	}

	/**
	 * Convert price using theme's currency conversion
	 */
	function convertPrice(priceInCents, fromCurrency, toCurrency) {
		if (!toCurrency || fromCurrency === toCurrency) {
			return priceInCents;
		}

		// Try theme's conversion function
		if (window.theme?.Currency?.convert) {
			try {
				return window.theme.Currency.convert(priceInCents, fromCurrency, toCurrency);
			} catch (e) {
				log('Theme currency conversion failed:', e);
			}
		}

		// Try other common conversion methods
		if (window.Currency?.convert) {
			try {
				return window.Currency.convert(priceInCents, fromCurrency, toCurrency);
			} catch (e) {
				log('Currency.convert failed:', e);
			}
		}

		// If no conversion available, return original
		return priceInCents;
	}

	/**
	 * Format price with correct currency symbol and conversion
	 */
	function formatPrice(priceInCents, currencyCode) {
		// Get current selected currency
		const currentCurrency = getCurrentCurrency();

		// Convert price if needed
		let finalPrice = priceInCents;
		let finalCurrency = currencyCode;

		if (currentCurrency && currentCurrency !== currencyCode) {
			finalPrice = convertPrice(priceInCents, currencyCode, currentCurrency);
			finalCurrency = currentCurrency;
		}

		const price = (finalPrice / 100).toFixed(2);

		// Currency symbol mapping
		const currencySymbols = {
			'USD': '$',
			'GBP': '£',
			'EUR': '€',
			'CAD': 'C$',
			'AUD': 'A$',
			'JPY': '¥',
			'CHF': 'CHF ',
			'SEK': 'kr',
			'NOK': 'kr',
			'DKK': 'kr.'
		};

		const symbol = currencySymbols[finalCurrency] || finalCurrency + ' ';
		return `${symbol}${price}`;
	}

	/**
	 * Transform GraphQL product data to match our expected format
	 */
	function transformGraphQLProduct(graphqlProduct) {
		const firstVariant = graphqlProduct.variants.edges[0]?.node;

		return {
			id: graphqlProduct.id.split('/').pop(), // Extract numeric ID
			title: graphqlProduct.title,
			handle: graphqlProduct.handle,
			description: graphqlProduct.description,
			vendor: graphqlProduct.vendor,
			product_type: graphqlProduct.productType,
			tags: graphqlProduct.tags,
			collections: graphqlProduct.collections.edges.map((edge) => ({
				id: edge.node.id.split('/').pop(),
				title: edge.node.title,
				handle: edge.node.handle
			})),
			price: firstVariant
				? Math.round(parseFloat(firstVariant.price.amount) * 100)
				: 0,
			price_min: Math.round(
				parseFloat(graphqlProduct.priceRange.minVariantPrice.amount) *
					100
			),
			price_max: Math.round(
				parseFloat(graphqlProduct.priceRange.maxVariantPrice.amount) *
					100
			),
			currency_code: graphqlProduct.priceRange.minVariantPrice.currencyCode,
			price_varies:
				graphqlProduct.priceRange.minVariantPrice.amount !==
				graphqlProduct.priceRange.maxVariantPrice.amount,
			compare_at_price: firstVariant?.compareAtPrice
				? Math.round(
						parseFloat(firstVariant.compareAtPrice.amount) * 100
					)
				: null,
			compare_at_price_min: graphqlProduct.compareAtPriceRange
				?.minVariantPrice
				? Math.round(
						parseFloat(
							graphqlProduct.compareAtPriceRange.minVariantPrice
								.amount
						) * 100
					)
				: null,
			compare_at_price_max: graphqlProduct.compareAtPriceRange
				?.maxVariantPrice
				? Math.round(
						parseFloat(
							graphqlProduct.compareAtPriceRange.maxVariantPrice
								.amount
						) * 100
					)
				: null,
			available: graphqlProduct.availableForSale,
			featured_image: graphqlProduct.featuredImage?.url || null,
			images: graphqlProduct.images.edges.map((edge) => edge.node.url),
			variants: graphqlProduct.variants.edges.map((edge) => ({
				id: edge.node.id.split('/').pop(),
				title: edge.node.title,
				price: Math.round(parseFloat(edge.node.price.amount) * 100),
				compare_at_price: edge.node.compareAtPrice
					? Math.round(
							parseFloat(edge.node.compareAtPrice.amount) * 100
						)
					: null,
				available: edge.node.availableForSale
			}))
		};
	}

	/**
	 * Create loading placeholder
	 */
	function createLoadingElement(type, code) {
		const element = document.createElement('div');
		element.className = `shortcode-placeholder ${config.loadingClass}`;
		element.innerHTML = `
			<div class="shortcode-spinner">
				<div class="shortcode-spinner__icon"></div>
				<span>Loading ${type}...</span>
			</div>
		`;
		element.dataset.type = type;
		element.dataset.code = code;
		return element;
	}

	/**
	 * Create error element
	 */
	function createErrorElement(type, code, message) {
		const element = document.createElement('div');
		element.className = `shortcode-placeholder ${config.errorClass}`;
		element.innerHTML = `
			<div class="shortcode-error-content">
				<strong>Error:</strong> ${message}
				<br><small>${type.charAt(0).toUpperCase() + type.slice(1)} ID: ${code}</small>
				<br><small>Debug: Check console for details</small>
			</div>
		`;
		return element;
	}

	/**
	 * Render floated product card HTML
	 */
	function renderFloatedProductCard(product, index = 0) {
		// Use the featured image or first available image
		let imageUrl =
			product.featured_image || (product.images && product.images[0]);

		if (imageUrl) {
			// Remove existing transformations but keep a reasonable max width
			imageUrl = imageUrl.split('?')[0]; // Remove existing query params
			imageUrl += '?v=' + Date.now() + '&width=400'; // Just set max width, let height scale naturally
		}

		// Get the ACTUAL collection name from Shopify collections
		let collection = null;

		if (product.collections && product.collections.length > 0) {
			// Use the first collection
			collection = product.collections[0].title;
		}

		// Format price using correct currency
		const price = formatPrice(product.price, product.currency_code);
		const compareAtPrice = product.compare_at_price
			? formatPrice(product.compare_at_price, product.currency_code)
			: null;

		// Determine if there's a sale
		const onSale = product.compare_at_price && product.compare_at_price > product.price;

		return `
			<div class="product-card-floated" data-product-id="${product.id}" data-index="${index}">
				<div class="product-card-floated__image-container">
				<div class="product-card-floated__image">
					<a href="/products/${product.handle}" class="product-card-floated__image-link">
					${
						imageUrl
							? `<img src="${imageUrl}" alt="${product.title}" class="product-card-floated__img" loading="lazy">`
							: `<div class="product-card-floated__image-placeholder">
						<svg width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
							<rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
							<circle cx="8.5" cy="8.5" r="1.5"/>
							<polyline points="21,15 16,10 5,21"/>
						</svg>
						</div>`
					}
					</a>
				</div>
				<div class="product-card-floated__content">
					<a href="/products/${product.handle}" class="product-card-floated__title">
						${product.title}
					</a>
					<div class="product-card-floated__price">
						${
							onSale
								? `<span class="product-card-floated__price--sale">${price}</span>
							<span class="product-card-floated__price--compare">${compareAtPrice}</span>`
								: `<span class="product-card-floated__price--regular">${price}</span>`
						}
					</div>
					${collection ? `<a href="/collections/${product.collections[0].handle}" class="product-card-floated__collection">${collection} collection</a>` : ''}
				</div>
				</div>
			</div>
		`;
	}

	/**
	 * Render collection grid HTML
	 */
	function renderCollectionGrid(collection, maxProducts = 12) {
		if (
			!collection ||
			!collection.products ||
			collection.products.length === 0
		) {
			return `
				<section class="collection-grid">
					<div class="collection-grid__container">
						<p>No products found in this collection.</p>
					</div>
				</section>
			`;
		}

		const products = collection.products.slice(0, maxProducts);
		const hasMore =
			collection.products_count > maxProducts ||
			collection.products.length === 16;

		const productsHtml = products
			.map((product) => {
				// Optimize image URL
				let imageUrl = product.featured_image;
				if (imageUrl) {
					imageUrl = imageUrl.split('?')[0] + '?width=300&height=300';
				}

				// Format price using correct currency
				const price = formatPrice(product.price, product.currency_code);
				const compareAtPrice = product.compare_at_price
					? formatPrice(product.compare_at_price, product.currency_code)
					: null;
				const onSale = product.compare_at_price && product.compare_at_price > product.price;

				return `
				<div class="collection-grid__item">
					<div class="collection-grid__image-container">
						<a href="/products/${product.handle}" class="collection-grid__image-link">
							${
								imageUrl
									? `<img src="${imageUrl}" alt="${product.title}" class="collection-grid__img" loading="lazy">`
									: `<div class="collection-grid__image-placeholder">
										<svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1">
											<rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
											<circle cx="8.5" cy="8.5" r="1.5"/>
											<polyline points="21,15 16,10 5,21"/>
										</svg>
									</div>`
							}
						</a>
					</div>
					<div class="collection-grid__content">
						<a href="/products/${product.handle}" class="collection-grid__title-link">
							${product.title}
						</a>
						<div class="collection-grid__price">
							${
								onSale
									? `<span class="collection-grid__price--sale">${price}</span>
								<span class="collection-grid__price--compare">${compareAtPrice}</span>`
									: `<span class="collection-grid__price--regular">${price}</span>`
							}
						</div>
					</div>
				</div>
			`;
			})
			.join('');

		return `
			<section class="collection-grid">
				<div class="collection-grid__container">
					<header class="collection-grid__header">
						<h2 class="collection-grid__title">${collection.title}</h2>
					</header>

					<div class="collection-grid__grid">
						${productsHtml}
					</div>

					${
						hasMore
							? `<div class="collection-grid__footer">
								<a href="/collections/${collection.handle}" class="collection-grid__view-all">
									View All Products →
								</a>
							</div>`
							: ''
					}
				</div>
			</section>
		`;
	}

	/**
	 * Process shortcodes in a container element
	 */
	async function processShortcodes(container) {
		if (!container) return;

		let content = container.innerHTML;
		const processedShortcodes = [];

		// Reset floated product index for each container
		floatedProductIndex = 0;

		// Find all product shortcodes
		let match;
		while ((match = config.productShortcodeRegex.exec(content)) !== null) {
			const [fullMatch, productGid] = match;
			processedShortcodes.push({
				type: 'product',
				gid: productGid.trim(),
				fullMatch: fullMatch,
				index: match.index
			});
		}
		config.productShortcodeRegex.lastIndex = 0;

		// Find all collection shortcodes
		while (
			(match = config.collectionShortcodeRegex.exec(content)) !== null
		) {
			const [fullMatch, collectionGid] = match;
			processedShortcodes.push({
				type: 'collection',
				gid: collectionGid.trim(),
				fullMatch: fullMatch,
				index: match.index
			});
		}
		config.collectionShortcodeRegex.lastIndex = 0;

		if (processedShortcodes.length === 0) {
			return;
		}

		log('Found shortcodes:', processedShortcodes);

		// Sort by index (descending) to replace from end to beginning
		processedShortcodes.sort((a, b) => b.index - a.index);

		// Replace shortcodes with loading placeholders first
		for (const shortcode of processedShortcodes) {
			const placeholder = createLoadingElement(
				shortcode.type,
				shortcode.gid
			);
			const placeholderHtml = placeholder.outerHTML;

			content =
				content.substring(0, shortcode.index) +
				placeholderHtml +
				content.substring(shortcode.index + shortcode.fullMatch.length);
		}

		// Update container with placeholders
		container.innerHTML = content;

		// Process each shortcode
		for (const shortcode of processedShortcodes) {
			try {
				const placeholder = container.querySelector(
					`[data-type="${shortcode.type}"][data-code="${shortcode.gid}"]`
				);

				if (!placeholder) continue;

				let data, html;

				if (shortcode.type === 'product') {
					data = await fetchProductById(shortcode.gid);
					html = renderFloatedProductCard(
						data,
						floatedProductIndex++
					);
				} else if (shortcode.type === 'collection') {
					data = await fetchCollectionById(shortcode.gid);
					html = renderCollectionGrid(data, 12);
				}

				// Replace placeholder with actual content
				const tempDiv = document.createElement('div');
				tempDiv.innerHTML = html;
				const newElement = tempDiv.firstElementChild;

				placeholder.parentNode.replaceChild(newElement, placeholder);
			} catch (error) {
				log('Error processing shortcode:', error);

				const placeholder = container.querySelector(
					`[data-type="${shortcode.type}"][data-code="${shortcode.gid}"]`
				);

				if (placeholder) {
					// Silently remove the placeholder - no error shown to user
					placeholder.remove();
				}
			}
		}
	}

	/**
	 * Add CSS styles for shortcode elements
	 */
	function addShortcodeStyles() {
		if (document.getElementById('blog-shortcodes-styles')) return;

		const style = document.createElement('style');
		style.id = 'blog-shortcodes-styles';
		style.textContent = `
		/* Shortcode Loading and Error Styles */
		.shortcode-placeholder {
			margin: 1.5rem 0;
			padding: 1rem;
			border: 1px solid #e0e0e0;
			border-radius: 8px;
			background: #f9f9f9;
		}
		
		.shortcode-spinner {
			display: flex;
			align-items: center;
			gap: 0.5rem;
			color: #666;
		}
		
		.shortcode-spinner__icon {
			width: 16px;
			height: 16px;
			border: 2px solid #ddd;
			border-top-color: #666;
			border-radius: 50%;
			animation: shortcode-spin 1s linear infinite;
		}
		
		@keyframes shortcode-spin {
			to { transform: rotate(360deg); }
		}
		
		.shortcode-error {
			background: #ffeaea;
			border-color: #ffcdd2;
			color: #c62828;
		}
		
		.shortcode-error-content {
			font-size: 0.9rem;
			line-height: 1.4;
		}

		/* Right-Floated Product Cards */
		.rte .product-card-floated,
		.article__body .product-card-floated {
			padding:10px;
			width: 350px !important;
			background: #ffffff;
			border-radius: 12px;
			box-shadow: 0 2px 12px rgba(0,0,0,0.08);
			margin: 0 0 1rem 1.5rem !important;
			float: right !important;
			clear: right;
			position: relative;
			max-width: 350px !important;
			overflow: hidden;
		}

		.product-card-floated a {
			border: 0px none !important;
		}

		.product-card-floated__image-container {
			position: relative;
			background: #f5f5f0 !important;
			border-radius: 12px 12px 0 0;
		}

		.product-card-floated__image {
			width: 100% !important;
			position: relative;
			background: #ffffff;
			display: flex !important;
			align-items: center;
			justify-content: center;
			overflow: hidden;
			min-height: 300px;
		}

		.product-card-floated__image-link {
			display: flex;
			width: 100%;
			height: 100%;
			align-items: center;
			justify-content: center;
		}

		.rte .product-card-floated__img,
		.article__body .product-card-floated__img {
			max-width: 100% !important;
			max-height: 100% !important;
			width: auto !important;
			height: auto !important;
			object-fit: contain !important;
			display: block !important;
		}

		.product-card-floated__image-placeholder {
			width: 100% !important;
			height: 200px !important;
			background: #f8f8f8 !important;
			display: flex !important;
			align-items: center;
			justify-content: center;
		}

		.product-card-floated__image-placeholder svg {
			width: 60px;
			height: 60px;
			opacity: 0.3;
		}

		.product-card-floated__content {
			background: #ffffff;
			border-radius: 0 0 12px 12px;
			text-align: left;
		}

		.product-card-floated__content a {
			border: 0px none !important;
		}

		.product-card-floated__title {
			display: block;
			font-size: 1.1rem;
			font-weight: 500;
			color: #1a1a1a;
		}

		.product-card-floated__title:hover {
			color: #007acc;
		}

		.product-card-floated__price {
			margin: 8px 0;
			font-size: 1.1rem;
			font-weight: 600;
			line-height: 1.2;
		}

		.product-card-floated__price--regular {
			color: #1a1a1a;
		}

		.product-card-floated__price--sale {
			color: #d32f2f;
			margin-right: 8px;
		}

		.product-card-floated__price--compare {
			color: #999;
			text-decoration: line-through;
			font-size: 0.9rem;
			font-weight: 400;
		}

		.product-card-floated__collection {
			color: #333 !important;
			font-weight: 300 !important;
			font-size: 0.9rem !important;
		}

		.product-card-floated__collection:hover {
			color: #007acc;
		}

		/* Collection Grid Styles */
		.collection-grid {
			margin: 2rem 0;
			padding: 2rem 0;
			border-top: 1px solid #e0e0e0;
			clear: both;
		}

		.collection-grid__container {
			max-width: 1200px;
			margin: 0 auto;
		}

		.collection-grid__header {
			text-align: center;
			margin-bottom: 2rem;
		}

		.collection-grid__title {
			font-size: 1.5rem;
			font-weight: 600;
			margin-bottom: 0.5rem;
			color: #1a1a1a;
		}

		.collection-grid__footer {
			text-align: center;
			margin-top: 2rem;
		}

		.collection-grid__view-all {
			display: inline-block;
			color: #007acc;
			text-decoration: underline;
			font-weight: 500;
			transition: color 0.2s ease;
		}

		.collection-grid__view-all:hover {
			color: #005999;
		}

		.collection-grid__grid {
			display: flex;
			flex-wrap: wrap;
			gap: 20px;
			margin-top: 1.5rem;
		}

		.collection-grid__item {
			flex: 0 0 calc(20% - 16px);
			background: #ffffff;
			border-radius: 8px;
			overflow: hidden;
			box-shadow: 0 2px 8px rgba(0,0,0,0.08);
			transition: transform 0.2s ease, box-shadow 0.2s ease;
		}

		.collection-grid__item:hover {
			transform: translateY(-2px);
			box-shadow: 0 4px 16px rgba(0,0,0,0.12);
		}

		.collection-grid__image-container {
			width: 100%;
			height: 200px;
			display: flex;
			align-items: center;
			justify-content: center;
			background-color: #f9f9f9;
			overflow: hidden;
			padding: 20px;
		}

		.collection-grid__image-link {
			display: flex;
			width: 100%;
			height: 100%;
			align-items: center;
			justify-content: center;
			text-decoration: none;
		}

		.collection-grid__img {
			max-width: 100%;
			max-height: 100%;
			width: auto;
			height: auto;
			object-fit: contain;
			display: block;
		}

		.collection-grid__image-placeholder {
			width: 100%;
			height: 100%;
			display: flex;
			align-items: center;
			justify-content: center;
			color: #999;
		}

		.collection-grid__image-placeholder svg {
			width: 60px;
			height: 60px;
			opacity: 0.3;
		}

		.collection-grid__content {
			width: 100%;
			height: 80px;
			padding: 15px;
			display: flex;
			flex-direction: column;
			justify-content: space-between;
		}

		.collection-grid__title-link {
			display: block;
			font-size: 14px;
			font-weight: 600;
			color: #1a1a1a;
			text-decoration: none;
			line-height: 1.3;
			white-space: nowrap;
			overflow: hidden;
			text-overflow: ellipsis;
			margin-bottom: 8px;
		}

		.collection-grid__title-link:hover {
			color: #007acc;
		}

		.collection-grid__price {
			font-size: 16px;
			font-weight: bold;
			line-height: 1.2;
			color: #007bff;
		}

		.collection-grid__price--regular {
			color: #007bff;
		}

		.collection-grid__price--sale {
			color: #d32f2f;
			margin-right: 6px;
		}

		.collection-grid__price--compare {
			color: #999;
			text-decoration: line-through;
			font-size: 0.85rem;
			font-weight: 400;
		}

		/* Responsive adjustments */
		@media (max-width: 1024px) {
			.collection-grid__item {
				flex: 0 0 calc(25% - 15px);
			}
		}

		@media (max-width: 768px) {
			.collection-grid__item {
				flex: 0 0 calc(33.333% - 13px);
			}
		}

		@media (max-width: 480px) {
			.collection-grid__item {
				flex: 0 0 calc(50% - 10px);
			}
		}

		/* Wider blog content on desktop */
		@media (min-width: 1200px) {
			.article {
				max-width: 900px;
			}

			.page-width--narrow {
				max-width: 900px;
			}
		}

		/* Prevent RTE from interfering */
		.rte .product-card-floated *,
		.article__body .product-card-floated * {
			box-sizing: border-box;
		}

		.rte .product-card-floated p,
		.article__body .product-card-floated p,
		.rte .product-card-floated div,
		.article__body .product-card-floated div {
			margin: 0 !important;
			padding: 0 !important;
		}

		/* Ensure collection grid items don't inherit RTE styles */
		.collection-grid * {
			box-sizing: border-box;
		}
	`;

		document.head.appendChild(style);
	}

	/**
	 * Re-process all shortcodes when currency changes
	 */
	function refreshCurrency() {
		log('Currency changed, refreshing prices...');

		// Re-process all existing product cards and collection grids
		const articleBody = document.querySelector('.article__body');
		if (articleBody) {
			// Find all existing shortcode elements and refresh their prices
			const productCards = articleBody.querySelectorAll('.product-card-floated');
			const collectionGrids = articleBody.querySelectorAll('.collection-grid');

			// For now, just trigger a full re-process (this will cache hit so it's fast)
			setTimeout(() => {
				processShortcodes(articleBody);
			}, 100);
		}
	}

	/**
	 * Initialize shortcode processing
	 */
	function init() {
		log('Initializing BlogShortcodes...');

		// Detect and log currency system (Motion theme specific)
		const currencySystem = detectCurrencySystem();
		const currentCurrency = getCurrentCurrency();
		log('Motion theme currency detection:', {
			system: currencySystem,
			currentCurrency: currentCurrency
		});

		// Add CSS for shortcode elements
		addShortcodeStyles();

		// Process shortcodes in article content
		const articleBody = document.querySelector('.article__body');
		if (articleBody) {
			log('Processing article body');
			processShortcodes(articleBody);
		}

		// Also check for RTE content areas (in case used elsewhere)
		const rteContainers = document.querySelectorAll(
			'.rte[data-shortcodes="true"]'
		);
		rteContainers.forEach((container) => {
			if (container !== articleBody) {
				log('Processing RTE container');
				processShortcodes(container);
			}
		});

		// Listen for currency changes (Motion theme specific)
		// Motion theme typically uses custom events or DOM changes
		document.addEventListener('currency:changed', refreshCurrency);
		document.addEventListener('theme:currency:changed', refreshCurrency);

		// Also listen for changes to currency selectors
		const currencySelectors = document.querySelectorAll('select[name="currency"], [data-currency-picker] select, .currency-selector select');
		currencySelectors.forEach(selector => {
			selector.addEventListener('change', refreshCurrency);
		});

		// Listen for country/region selector changes (often used with currency)
		const countrySelectors = document.querySelectorAll('select[name="country"], [data-country-picker] select, .country-selector select');
		countrySelectors.forEach(selector => {
			selector.addEventListener('change', () => {
				// Delay to allow theme to update currency
				setTimeout(refreshCurrency, 500);
			});
		});
	}

	// Public API
	return {
		init: init,
		processShortcodes: processShortcodes,
		config: config
	};
})();

// Auto-initialize when DOM is ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', window.BlogShortcodes.init);
} else {
	window.BlogShortcodes.init();
}
